import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useTranslation } from "react-i18next";
import i18n, { setLanguage } from "../i18n/i18n.js";
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { MARKET } from '../utils/market.js';
import {
  Star, User, Mail, Lock, Trash2, Bookmark,
  Save, Globe, Shield, LogOut, Edit2,
  Check, X, AlertTriangle, Calendar, Clock,
  Heart, MessageCircle, Monitor, BellOff, Crown, Eye,
  Archive, Search
} from "lucide-react";
import "./Settings.css";
import SupportChatPanel from "./SupportChatPanel.jsx";

import { toast } from '../utils/toast.js';
const MATCH_CATEGORIES = ["education", "scholarship", "fellowship", "erasmusplus", "training", "grant", "internship", "conference", "online", "volunteering", "event", "award"];

const DEFAULT_AVATAR = "/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png";
const DEFAULT_LANGUAGE = 'hy';

function Settings() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'account');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [trashPosts, setTrashPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [savedLikedFilter, setSavedLikedFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [language, setLanguageState] = useState(() => localStorage.getItem('language') || DEFAULT_LANGUAGE);
  // Content language: which language programs/opportunities are shown in. Independent of
  // the interface language above — falls back to it until the user picks something else,
  // so "UI in Russian, but also show English-language programs" is possible.
  const [contentLanguage, setContentLanguageState] = useState(() => localStorage.getItem('contentLanguage') || localStorage.getItem('language') || DEFAULT_LANGUAGE);
  const [marketPref, setMarketPref] = useState(() => {
    try { return localStorage.getItem('greenwich_market_pref') || MARKET; } catch (e) { return MARKET; }
  });

  // Saves the manual choice (always wins over the auto-detect redirect in
  // public/index.html) and sends the browser to the matching domain.
  const handleMarketChange = (e) => {
    const next = e.target.value;
    setMarketPref(next);
    try { localStorage.setItem('greenwich_market_pref', next); } catch (err) {}
    const targetHost = next === 'FR' ? 'fr.greenwich.am' : 'greenwich.am';
    if (window.location.hostname.toLowerCase() !== targetHost) {
      // localStorage doesn't carry over to the other domain — pass the choice
      // as ?market=.. too, so public/index.html's inline script can save it
      // to that origin's own localStorage before its auto-detect can run.
      const qs = new URLSearchParams(window.location.search);
      qs.set('market', next);
      window.location.href = `https://${targetHost}${window.location.pathname}?${qs.toString()}`;
    }
  };
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);

  const [accountForm, setAccountForm] = useState({
    name: user?.name || '', surname: user?.surname || '',
    email: user?.email || '', phone: user?.phone || '',
    bio: user?.bio || '', location: user?.location || '',
    profileColor: user?.profileColor || '#32614d'
  });

  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  const [proInfo, setProInfo] = useState(null);
  const [frameChoice, setFrameChoice] = useState('');
  const [designChoice, setDesignChoice] = useState('');
  const [savingCustomization, setSavingCustomization] = useState(false);
  const [profileViews, setProfileViews] = useState([]);
  const [profileViewsError, setProfileViewsError] = useState('');
  const [loadingProfileViews, setLoadingProfileViews] = useState(false);
  const [viewsCountToday, setViewsCountToday] = useState(null);
  const [channelChoice, setChannelChoice] = useState('platform');
  const [savingChannel, setSavingChannel] = useState(false);
  const [matchPrefs, setMatchPrefs] = useState({ matchDeliveryHour: 9, matchReminders: false, matchDetailLevel: 'brief', matchSource: 'ai', matchCategories: [] });
  const [savingMatchPrefs, setSavingMatchPrefs] = useState(false);

  // ── Billing & refunds ──
  const [billingHistory, setBillingHistory] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null); // payment being refunded
  const [refundReason, setRefundReason] = useState('');
  const [refundRating, setRefundRating] = useState(0);
  const [refundComment, setRefundComment] = useState('');
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const REFUND_WINDOW_DAYS = 7;
  const REFUND_REASONS = ['not_what_expected', 'found_cheaper_elsewhere', 'technical_issues', 'accidental_purchase', 'no_longer_needed', 'bought_wrong_plan', 'unsatisfied_with_features', 'other'];

  // ── Interactions (who can message/comment, restricted accounts) ──
  const [interactionSettings, setInteractionSettings] = useState({ messagePermission: 'followers', commentPermission: 'everyone' });
  const [savingInteractions, setSavingInteractions] = useState(false);
  const [restrictedUsers, setRestrictedUsers] = useState([]);
  const [restrictSearch, setRestrictSearch] = useState('');
  const [restrictResults, setRestrictResults] = useState([]);
  const [restrictSearching, setRestrictSearching] = useState(false);

  // ── Archive ──
  const [archivedPosts, setArchivedPosts] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);

  // ── Activity ──
  const [activity, setActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityFilter, setActivityFilter] = useState('liked');

  // ── Accessibility ──
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem('reduceMotion') === 'true');

  // ── Data & download ──
  const [exportingData, setExportingData] = useState(false);

  useEffect(() => {
    if (activeTab === 'saved') { fetchSavedPosts(); fetchLikedPosts(); }
    if (activeTab === 'pro') { fetchProInfo(); fetchViewsCountToday(); fetchBillingHistory(); }
    if (activeTab === 'interactions') { fetchInteractionSettings(); fetchRestrictedUsers(); }
    if (activeTab === 'archive') { fetchArchivedPosts(); }
    if (activeTab === 'activity') { fetchActivity(); }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'pro' && proInfo?.isPro) fetchProfileViews();
  }, [activeTab, proInfo?.isPro]);

  useEffect(() => {
    document.body.classList.toggle('reduce-motion', reduceMotion);
  }, [reduceMotion]);

  const API = `${GREENWICH_BACKEND_URL}/api`;
  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

  const fetchSavedPosts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/posts/saved`, { headers: authHeaders() });
      if (res.ok) { const d = await res.json(); setSavedPosts(d.posts || d.data || (Array.isArray(d) ? d : [])); }
      else setSavedPosts([]);
    } catch { setSavedPosts([]); } finally { setLoading(false); }
  };

  const fetchLikedPosts = async () => {
    try {
      const res = await fetch(`${API}/posts/liked`, { headers: authHeaders() });
      if (res.ok) { const d = await res.json(); setLikedPosts(d.posts || d.data || (Array.isArray(d) ? d : [])); }
      else setLikedPosts([]);
    } catch { setLikedPosts([]); }
  };

  const fetchProInfo = async () => {
    try {
      const res = await fetch(`${API}/user/profile`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) {
        setProInfo(d.user);
        setFrameChoice(d.user.customFrame || '');
        setDesignChoice(d.user.customProfileDesign || '');
        setChannelChoice(d.user.notificationChannel || 'platform');
        setMatchPrefs({
          matchDeliveryHour: d.user.matchDeliveryHour ?? 9,
          matchReminders: !!d.user.matchReminders,
          matchDetailLevel: d.user.matchDetailLevel || 'brief',
          matchSource: d.user.matchSource || 'ai',
          matchCategories: d.user.matchCategories || [],
        });
      }
    } catch {}
  };

  const fetchBillingHistory = async () => {
    try {
      setLoadingBilling(true);
      const res = await fetch(`${API}/payments/history`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setBillingHistory(d.payments.filter((p) => p.purpose === 'pro'));
    } catch {} finally { setLoadingBilling(false); }
  };

  const refundEligible = (p) => {
    if (p.status !== 'paid' || p.refundStatus !== 'none') return false;
    const ageMs = Date.now() - new Date(p.paidAt).getTime();
    return ageMs <= REFUND_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  };

  const openRefundModal = (payment) => {
    setRefundTarget(payment);
    setRefundReason('');
    setRefundRating(0);
    setRefundComment('');
  };

  const handleSubmitRefund = async () => {
    if (!refundTarget || submittingRefund) return;
    if (!refundReason) return toast(t("settings.pro.selectReason"));
    setSubmittingRefund(true);
    try {
      const res = await fetch(`${API}/payments/${refundTarget.orderId}/refund-request`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ reason: refundReason, rating: refundRating || undefined, comment: refundComment || undefined }),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        toast(t("settings.pro.refundSubmitted"));
        setRefundTarget(null);
        fetchBillingHistory();
      } else {
        toast(d.error || t("settings.pro.refundError"));
      }
    } catch {
      toast(t("settings.pro.refundError"));
    } finally {
      setSubmittingRefund(false);
    }
  };

  const fetchProfileViews = async () => {
    try {
      setLoadingProfileViews(true);
      setProfileViewsError('');
      const res = await fetch(`${API}/gamification/profile-views`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setProfileViews(d.views || []);
      else { setProfileViews([]); setProfileViewsError(d.error || ''); }
    } catch {
      setProfileViews([]);
    } finally {
      setLoadingProfileViews(false);
    }
  };

  const fetchViewsCountToday = async () => {
    try {
      const res = await fetch(`${API}/gamification/profile-views/count-today`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setViewsCountToday(d.count);
    } catch {}
  };

  // ── Interactions (message/comment permissions, restricted accounts) ──
  const fetchInteractionSettings = async () => {
    try {
      const res = await fetch(`${API}/user/interaction-settings`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setInteractionSettings(d.settings);
    } catch {}
  };
  const saveInteractionSettings = async (patch) => {
    const next = { ...interactionSettings, ...patch };
    setInteractionSettings(next);
    try {
      setSavingInteractions(true);
      const res = await fetch(`${API}/user/interaction-settings`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(patch) });
      const d = await res.json();
      if (!res.ok || !d.success) toast(d.error || t('settings.toasts.failedToSave'));
    } catch { toast(t('settings.toasts.failedToSave')); } finally { setSavingInteractions(false); }
  };
  const fetchRestrictedUsers = async () => {
    try {
      const res = await fetch(`${API}/user/restricted`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setRestrictedUsers(d.restricted || []);
    } catch {}
  };
  const searchForRestrict = async (q) => {
    setRestrictSearch(q);
    if (!q.trim()) { setRestrictResults([]); return; }
    try {
      setRestrictSearching(true);
      const res = await fetch(`${API}/search?q=${encodeURIComponent(q)}&type=users`, { headers: authHeaders() });
      const d = await res.json();
      const users = d.users || d.results?.users || (Array.isArray(d.results) ? d.results : []) || [];
      setRestrictResults(users.filter(u => u._id !== user?._id));
    } catch { setRestrictResults([]); } finally { setRestrictSearching(false); }
  };
  const restrictUser = async (userId) => {
    try {
      const res = await fetch(`${API}/user/restrict/${userId}`, { method: 'POST', headers: authHeaders() });
      const d = await res.json();
      if (d.success) { fetchRestrictedUsers(); setRestrictSearch(''); setRestrictResults([]); toast(t('settings.toasts.userRestricted')); }
      else toast(d.error || t('settings.toasts.failedToRestrict'));
    } catch { toast(t('settings.toasts.failedToRestrict')); }
  };
  const unrestrictUser = async (userId) => {
    try {
      const res = await fetch(`${API}/user/restrict/${userId}`, { method: 'DELETE', headers: authHeaders() });
      const d = await res.json();
      if (d.success) { setRestrictedUsers(prev => prev.filter(u => u._id !== userId)); toast(t('settings.toasts.userUnrestricted')); }
      else toast(d.error || t('settings.toasts.failedToUnrestrict'));
    } catch { toast(t('settings.toasts.failedToUnrestrict')); }
  };

  // ── Archive ──
  const fetchArchivedPosts = async () => {
    try {
      setLoadingArchive(true);
      const res = await fetch(`${API}/posts/archived`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setArchivedPosts(d.posts || []);
    } catch {} finally { setLoadingArchive(false); }
  };
  const unarchivePost = async (postId) => {
    try {
      const res = await fetch(`${API}/posts/${postId}/archive`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ archived: false }) });
      const d = await res.json();
      if (d.success) { setArchivedPosts(prev => prev.filter(p => p._id !== postId)); toast(t('settings.toasts.postUnarchived')); }
      else toast(d.error || t('settings.toasts.failedToUnarchive'));
    } catch { toast(t('settings.toasts.failedToUnarchive')); }
  };

  // ── Activity ──
  const fetchActivity = async () => {
    try {
      setLoadingActivity(true);
      const res = await fetch(`${API}/user/activity`, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) setActivity(d.activity);
    } catch {} finally { setLoadingActivity(false); }
  };

  // ── Data export ── (scope: 'all' | 'profile' | 'programs' | 'buddy' | 'messages') ──
  const [exportingScope, setExportingScope] = useState(null);
  const handleDataExport = async (scope = 'all') => {
    try {
      setExportingData(true);
      setExportingScope(scope);
      const res = await fetch(`${API}/user/data-export?scope=${scope}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `greenwich-data-export-${scope}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast(t('settings.toasts.failedToExport')); } finally { setExportingData(false); setExportingScope(null); }
  };

  const handleSaveCustomization = async () => {
    try {
      setSavingCustomization(true);
      const res = await fetch(`${API}/user/customization`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ customFrame: frameChoice, customProfileDesign: designChoice }),
      });
      const d = await res.json();
      if (res.ok && d.success) {
        toast(t("settings.pro.customizationSaved"));
        fetchProInfo();
      } else {
        toast(d.error || t("settings.pro.customizationError"));
      }
    } catch {
      toast(t("settings.pro.customizationError"));
    } finally {
      setSavingCustomization(false);
    }
  };

  const [linkingTelegram, setLinkingTelegram] = useState(false);

  const handleLinkTelegram = async () => {
    try {
      setLinkingTelegram(true);
      const res = await fetch(`${API}/user/telegram/link-token`, { method: 'POST', headers: authHeaders() });
      const d = await res.json();
      if (res.ok && d.success) {
        window.open(d.deepLink, '_blank');
      } else {
        toast(d.error || t("settings.pro.customizationError"));
      }
    } catch {
      toast(t("settings.pro.customizationError"));
    } finally {
      setLinkingTelegram(false);
    }
  };

  const handleSaveChannel = async (channel) => {
    if (channel === 'telegram' && !proInfo?.telegramChatId) { handleLinkTelegram(); return; }
    try {
      setSavingChannel(true);
      const res = await fetch(`${API}/user/notification-channel`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ channel }),
      });
      const d = await res.json();
      if (res.ok && d.success) setChannelChoice(channel);
      else toast(d.error || t("settings.pro.customizationError"));
    } catch {
      toast(t("settings.pro.customizationError"));
    } finally {
      setSavingChannel(false);
    }
  };

  const handleSaveMatchPrefs = async (patch) => {
    const next = { ...matchPrefs, ...patch };
    setMatchPrefs(next);
    try {
      setSavingMatchPrefs(true);
      const res = await fetch(`${API}/user/match-preferences`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(patch),
      });
      const d = await res.json();
      if (!res.ok || !d.success) toast(d.error || t("settings.pro.customizationError"));
    } catch {
      toast(t("settings.pro.customizationError"));
    } finally {
      setSavingMatchPrefs(false);
    }
  };

  const postAction = async (url) => {
    try { await fetch(url, { method: 'POST', headers: authHeaders() }); fetchSavedPosts(); fetchLikedPosts(); } catch {}
  };

  const getImageUrl = (url) => {
    if (!url || url === "null" || url === "undefined") return DEFAULT_AVATAR;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/")) return `${GREENWICH_BACKEND_URL}${url}`;
    return `${GREENWICH_BACKEND_URL}/uploads/${url}`;
  };

  const handleSaveAccount = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API}/auth/profile`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(accountForm) });
      if (res.ok) { const d = await res.json(); if (d.success) toast(t("settings.account.profileUpdated")); }
    } catch { toast(t("settings.account.profileUpdateError")); } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) { toast(t("settings.account.passwordFieldsRequired")); return; }
    if (newPassword.length < 8) { toast(t("settings.account.passwordTooShort")); return; }
    if (newPassword !== confirmPassword) { toast(t("settings.account.passwordMismatch")); return; }
    try {
      setChangingPassword(true);
      const res = await fetch(`${API}/auth/change-password`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ currentPassword, newPassword }) });
      const d = await res.json();
      if (res.ok && d.success) {
        toast(t("settings.account.passwordUpdated"));
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast(d.error || t("settings.account.passwordUpdateFailed"));
      }
    } catch { toast(t("settings.account.passwordUpdateFailed")); } finally { setChangingPassword(false); }
  };

  const handleLogout = () => { if (window.confirm(t("settings.confirmLogout"))) logout(); };

  const handleLanguageChange = (e) => {
    const next = e.target.value;
    setLanguageState(next); localStorage.setItem('language', next); i18n.changeLanguage(next);
    if (typeof setLanguage === 'function') setLanguage(next);
  };

  const handleContentLanguageChange = (e) => {
    const next = e.target.value;
    setContentLanguageState(next);
    localStorage.setItem('contentLanguage', next);
    window.dispatchEvent(new CustomEvent('contentLanguageChanged', { detail: next }));
  };

  const handleDeleteAccount = async () => {
    try {
      setShowFinalConfirm(false);
      const res = await fetch(`${API}/auth/delete-account`, {
        method: "DELETE", headers: authHeaders(),
        body: JSON.stringify({ reason: deleteReason === "other" ? customReason : deleteReason })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d?.error) { toast(d?.error || t("settings.deleteModal.error")); return; }
      ['token','token_expiry','user'].forEach(k => localStorage.removeItem(k));
      logout(); window.location.href = "/registration";
    } catch { toast(t("settings.deleteModal.error")); }
  };

  const handleTabChange = (tab) => { setActiveTab(tab); setMobileSidebarOpen(false); };

  // ── Styles ──────────────────────────────
  const inputSt = {
    width: '100%', padding: '11px 13px', border: '1.5px solid rgba(255,255,255,0.16)',
    borderRadius: 12, fontSize: 14, fontFamily: 'inherit', outline: 'none',
    background: 'rgba(255,255,255,0.06)', color: '#fdfdf9', boxSizing: 'border-box', transition: 'border-color .2s, box-shadow .2s, background .2s',
  };
  const fi = (e) => { e.target.style.borderColor = 'rgba(255,203,71,0.6)'; e.target.style.background = 'rgba(255,255,255,0.09)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,203,71,0.15)'; };
  const fb = (e) => { e.target.style.borderColor = 'rgba(255,255,255,0.16)'; e.target.style.background = 'rgba(255,255,255,0.06)'; e.target.style.boxShadow = 'none'; };

  const card = {
    background: 'linear-gradient(155deg, rgba(255,255,255,0.14), rgba(255,255,255,0.05))',
    backdropFilter: 'blur(22px) saturate(160%)', WebkitBackdropFilter: 'blur(22px) saturate(160%)',
    borderRadius: 18, padding: 'clamp(16px,3vw,24px)', border: '1px solid rgba(255,255,255,0.16)',
    boxShadow: '0 8px 30px rgba(8,22,16,0.35), 0 1px 0 rgba(255,255,255,0.18) inset', marginBottom: 16,
  };

  // ── Render sections ──────────────────────
  const renderAccount = () => (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-display, inherit)' }}><User size={18} color="#ffd873" /> {t("settings.account.title")}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t("settings.account.subtitle")}</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }} className="st-form-grid">
        {[
          { label: t("settings.account.firstName"), field: 'name', type: 'text' },
          { label: t("settings.account.lastName"), field: 'surname', type: 'text' },
          { label: t("settings.account.email"), field: 'email', type: 'email' },
          { label: t("settings.account.phone"), field: 'phone', type: 'text' },
        ].map(({ label, field, type }) => (
          <div key={field}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 5 }}>{label}</label>
            <input type={type} value={accountForm[field]} onChange={e => setAccountForm(p => ({ ...p, [field]: e.target.value }))} placeholder={label} style={inputSt} onFocus={fi} onBlur={fb} />
          </div>
        ))}
        <div style={{ gridColumn: '1/-1' }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 5 }}>{t("settings.account.location")}</label>
          <input type="text" value={accountForm.location} onChange={e => setAccountForm(p => ({ ...p, location: e.target.value }))} placeholder={t("settings.account.location")} style={inputSt} onFocus={fi} onBlur={fb} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 5 }}>{t("settings.account.bio")}</label>
          <textarea value={accountForm.bio} onChange={e => setAccountForm(p => ({ ...p, bio: e.target.value }))} placeholder={t("settings.account.bioPlaceholder")} rows="3" style={{ ...inputSt, resize: 'vertical' }} onFocus={fi} onBlur={fb} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 5 }}>{t("settings.account.profileColor")}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="color" value={accountForm.profileColor} onChange={e => setAccountForm(p => ({ ...p, profileColor: e.target.value }))}
              style={{ width: 44, height: 38, padding: 2, border: '1.5px solid rgba(255,255,255,0.16)', borderRadius: 10, background: 'rgba(255,255,255,0.06)', cursor: 'pointer' }} />
            <input type="text" value={accountForm.profileColor} onChange={e => setAccountForm(p => ({ ...p, profileColor: e.target.value }))}
              placeholder="#32614d" maxLength={7} style={{ ...inputSt, flex: 1 }} onFocus={fi} onBlur={fb} />
          </div>
          <p style={{ fontSize: 11.5, color: 'rgba(234,243,238,0.55)', margin: '6px 0 0' }}>{t("settings.account.profileColorHint")}</p>
        </div>
      </div>
      <button onClick={handleSaveAccount} disabled={saving} className="st-btn-primary"
        style={{ marginTop: 16, opacity: saving ? 0.7 : 1 }}>
        <Save size={15} /> {saving ? t("common.saving") : t("settings.account.saveChanges")}
      </button>

      <div style={{ ...card, marginTop: 24, marginBottom: 0 }}>
        <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><Lock size={16} color="#ffd873" /> {t("settings.account.changePasswordTitle")}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: '0 0 16px' }}>{t("settings.account.changePasswordSubtitle")}</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }} className="st-form-grid">
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 5 }}>{t("settings.account.currentPassword")}</label>
            <input type="password" value={passwordForm.currentPassword} onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))} placeholder={t("settings.account.currentPassword")} style={inputSt} onFocus={fi} onBlur={fb} autoComplete="current-password" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 5 }}>{t("settings.account.newPassword")}</label>
            <input type="password" value={passwordForm.newPassword} onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))} placeholder={t("settings.account.newPasswordPlaceholder")} style={inputSt} onFocus={fi} onBlur={fb} autoComplete="new-password" />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 5 }}>{t("settings.account.confirmNewPassword")}</label>
            <input type="password" value={passwordForm.confirmPassword} onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))} placeholder={t("settings.account.confirmNewPasswordPlaceholder")} style={inputSt} onFocus={fi} onBlur={fb} autoComplete="new-password" />
          </div>
        </div>
        <button onClick={handleChangePassword} disabled={changingPassword} className="st-btn-primary"
          style={{ marginTop: 16, opacity: changingPassword ? 0.7 : 1 }}>
          <Lock size={15} /> {changingPassword ? t("common.saving") : t("settings.account.updatePassword")}
        </button>
      </div>

      <div style={{ ...card, marginTop: 16, marginBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><Monitor size={16} color="#ffd873" /> {t("settings.account.activeSessionsTitle")}</h3>
          <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t("settings.account.activeSessionsSubtitle")}</p>
        </div>
        <button onClick={() => navigate('/active-sessions')} className="st-btn-secondary" style={{ flexShrink: 0 }}>
          {t("settings.account.manage")}
        </button>
      </div>
    </div>
  );

  const renderSaved = () => {
    const allPosts = [...savedPosts, ...likedPosts].filter((p, i, s) => i === s.findIndex(x => x?._id === p?._id));
    const filtered = allPosts.filter(p => {
      const sv = savedPosts.some(x => x?._id === p?._id);
      const lk = likedPosts.some(x => x?._id === p?._id);
      if (savedLikedFilter === 'saved') return sv;
      if (savedLikedFilter === 'liked') return lk;
      return true;
    });

    if (loading) return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.16)', borderTopColor: '#ffcb47', borderRadius: '50%', animation: 'st-spin 1s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: 'rgba(234,243,238,0.65)', fontSize: 14 }}>{t("settings.savedLiked.loadingPosts")}</p>
      </div>
    );

    if (allPosts.length === 0) return (
      <div style={{ textAlign: 'center', padding: '48px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16, color: 'rgba(255,255,255,0.25)' }}><Heart size={40} /><Bookmark size={40} /></div>
        <h3 style={{ color: '#fdfdf9', marginBottom: 8 }}>{t("settings.savedLiked.emptyTitle")}</h3>
        <p style={{ color: 'rgba(234,243,238,0.5)', fontSize: 14, marginBottom: 16 }}>{t("settings.savedLiked.emptySubtitle")}</p>
        <button onClick={() => { fetchSavedPosts(); fetchLikedPosts(); }} className="st-btn-primary">
          {t("common.refresh")}
        </button>
      </div>
    );

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <h3 style={{ fontSize: 17, fontWeight: 800, color: '#fdfdf9', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Heart size={17} color="#ff9b95" /><Bookmark size={17} color="#ffd873" /> {t("settings.savedLiked.title")} ({allPosts.length})
          </h3>
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
            {['all','saved','liked'].map(f => (
              <button key={f} onClick={() => setSavedLikedFilter(f)} className={`st-pill${savedLikedFilter === f ? ' active' : ''}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, fontSize: 13, color: 'rgba(234,243,238,0.65)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Bookmark size={13} color="#ffd873" /> {savedPosts.length}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Heart size={13} color="#ff9b95" /> {likedPosts.length}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }} className="st-posts-grid">
          {filtered.map(post => {
            if (!post?._id) return null;
            const isSaved = savedPosts.some(x => x?._id === post._id);
            const isLiked = likedPosts.some(x => x?._id === post._id);
            const likeCount = Array.isArray(post.likes) ? post.likes.length : (post.likeCount || 0);
            const saveCount = Array.isArray(post.saves) ? post.saves.length : (post.saveCount || 0);
            const commentCount = Array.isArray(post.comments) ? post.comments.length : (post.commentCount || 0);
            return (
              <div key={post._id} className="st-list-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
                  <img src={post.author?.profileImage ? getImageUrl(post.author.profileImage) : DEFAULT_AVATAR} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.18)' }} onError={e => e.target.src = DEFAULT_AVATAR} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#fdfdf9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.author?.name || t("settings.savedLiked.unknownAuthor")} {post.author?.surname || ''}</div>
                    <div style={{ fontSize: 11, color: 'rgba(234,243,238,0.5)', display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={10} /> {new Date(post.createdAt).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', flexShrink: 0 }}>
                    {isSaved && <span style={{ background: 'rgba(255,203,71,0.16)', color: '#ffd873', border: '1px solid rgba(255,203,71,0.3)', borderRadius: 10, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>{t("settings.savedLiked.badgeSaved")}</span>}
                    {isLiked && <span style={{ background: 'rgba(239,68,68,0.16)', color: '#ff9b95', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>{t("settings.savedLiked.badgeLiked")}</span>}
                  </div>
                </div>
                {post.images?.[0] && <img src={getImageUrl(post.images[0])} alt="Post" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />}
                {post.content && <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.85)', lineHeight: 1.5, padding: '8px 12px', margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content}</p>}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '4px 0' }}>
                  {[
                    { icon: <Heart size={15} fill={isLiked ? '#ffd873' : 'none'} stroke="#ffd873" />, count: likeCount, action: () => postAction(`${API}/posts/${post._id}/like`) },
                    { icon: <MessageCircle size={15} stroke="#ffd873" />, count: commentCount, action: () => window.open(`/post/${post._id}`, '_blank') },
                    { icon: <Bookmark size={15} fill={isSaved ? '#ffd873' : 'none'} stroke="#ffd873" />, count: saveCount, action: () => postAction(`${API}/posts/${post._id}/save`) },
                  ].map((btn, i) => (
                    <button key={i} onClick={btn.action} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 4px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: '#ffd873', fontFamily: 'inherit' }}>
                      {btn.icon}<span>{btn.count}</span>
                    </button>
                  ))}
                  <button onClick={() => { const url = `${window.location.origin}/post/${post._id}`; navigator.clipboard?.writeText(url).catch(() => {}); }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '6px 4px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: '#ffd873', fontFamily: 'inherit' }}>
                    <img src="/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png" alt={t("postModal.share")} style={{ width: 15, height: 15, transform: 'rotate(30deg)' }} />
                    <span>{t("postModal.share")}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPrivacy = () => (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><Shield size={18} color="#ffd873" /> {t("settings.privacy.title")}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t("settings.privacy.subtitle")}</p>
      </div>
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><Globe size={15} color="#ffd873" /> {t("settings.privacy.language")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.languageSubtitle")}</div>
          </div>
          <select value={language} onChange={handleLanguageChange} className="st-select">
            {[['en','English'],['hy','Հայերեն'],['ru','Русский'],['es','Español'],['de','Deutsch'],['pt','Português'],['fr','Français']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><Globe size={15} color="#ffd873" /> {t("settings.privacy.contentLanguage")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.contentLanguageSubtitle")}</div>
          </div>
          <select value={contentLanguage} onChange={handleContentLanguageChange} className="st-select">
            {[['en','English'],['hy','Հայերեն'],['ru','Русский']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><Globe size={15} color="#ffd873" /> {t("settings.privacy.market")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.marketSubtitle")}</div>
          </div>
          <select value={marketPref} onChange={handleMarketChange} className="st-select">
            <option value="AM">🇦🇲 Armenia</option>
            <option value="FR">🇫🇷 France</option>
          </select>
        </div>
        <button onClick={() => navigate('/follow-settings')} className="st-row-btn">
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><Lock size={15} color="#ffd873" /> {t("settings.privacy.privateAccount")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.privateAccountSubtitle")}</div>
          </div>
          <span style={{ color: '#ffd873', fontSize: 13, fontWeight: 700 }}>{t("settings.privacy.manage")}</span>
        </button>
        <button onClick={() => navigate('/notification-settings')} className="st-row-btn">
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><BellOff size={15} color="#ffd873" /> {t("settings.privacy.notificationsRow")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.notificationsRowSubtitle")}</div>
          </div>
          <span style={{ color: '#ffd873', fontSize: 13, fontWeight: 700 }}>{t("settings.privacy.manage")}</span>
        </button>
        <button onClick={() => navigate('/security-settings')} className="st-row-btn">
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><Shield size={15} color="#ffd873" /> {t("settings.privacy.security")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.securitySubtitle")}</div>
          </div>
          <span style={{ color: '#ffd873', fontSize: 13, fontWeight: 700 }}>{t("settings.privacy.manage")}</span>
        </button>
        <button onClick={() => navigate('/blocked-users')} className="st-row-btn">
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><Shield size={15} color="#ffd873" /> {t("settings.privacy.blockedAccounts")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.blockedAccountsSubtitle")}</div>
          </div>
          <span style={{ color: '#ffd873', fontSize: 13, fontWeight: 700 }}>{t("settings.privacy.manage")}</span>
        </button>
        <button onClick={() => navigate('/muted-users')} className="st-row-btn" style={{ borderBottom: 'none' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', display: 'flex', alignItems: 'center', gap: 6 }}><BellOff size={15} color="#ffd873" /> {t("settings.privacy.mutedAccounts")}</div>
            <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', marginTop: 2 }}>{t("settings.privacy.mutedAccountsSubtitle")}</div>
          </div>
          <span style={{ color: '#ffd873', fontSize: 13, fontWeight: 700 }}>{t("settings.privacy.manage")}</span>
        </button>
      </div>
      <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: 16, border: '1.5px solid rgba(239,68,68,0.3)', padding: '16px', backdropFilter: 'blur(14px)' }}>
        <h4 style={{ fontSize: 14, fontWeight: 800, color: '#ff9b95', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={15} /> {t("settings.privacy.dangerZone")}</h4>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={handleLogout} className="st-btn-secondary">
            <LogOut size={14} /> {t("common.logout")}
          </button>
          <button onClick={() => setShowDeleteModal(true)} className="st-btn-danger">
            <Trash2 size={14} /> {t("common.deleteAccount")}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Radio-pill group used by the Interactions tab ──
  const PermissionPicker = ({ value, options, onChange, disabled }) => (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map(opt => (
        <button key={opt.key} disabled={disabled} onClick={() => onChange(opt.key)} className={`st-picker-btn${value === opt.key ? ' active' : ''}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );

  const renderInteractions = () => (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><MessageCircle size={18} color="#ffd873" /> {t('settings.interactions.title')}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t('settings.interactions.subtitle')}</p>
      </div>

      <div style={card}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t('settings.interactions.messagesHeading')}</h4>
        <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 12px' }}>{t('settings.interactions.messagesSubtitle')}</p>
        <PermissionPicker
          value={interactionSettings.messagePermission}
          disabled={savingInteractions}
          onChange={(v) => saveInteractionSettings({ messagePermission: v })}
          options={[
            { key: 'everyone', label: t('settings.interactions.everyone') },
            { key: 'followers', label: t('settings.interactions.followersBack') },
            { key: 'nobody', label: t('settings.interactions.noOne') },
          ]}
        />
      </div>

      <div style={card}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t('settings.interactions.commentsHeading')}</h4>
        <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 12px' }}>{t('settings.interactions.commentsSubtitle')}</p>
        <PermissionPicker
          value={interactionSettings.commentPermission}
          disabled={savingInteractions}
          onChange={(v) => saveInteractionSettings({ commentPermission: v })}
          options={[
            { key: 'everyone', label: t('settings.interactions.everyone') },
            { key: 'followers', label: t('settings.interactions.yourFollowers') },
            { key: 'off', label: t('settings.interactions.turnOffComments') },
          ]}
        />
      </div>

      <div style={card}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t('settings.interactions.restrictedHeading')}</h4>
        <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 12px' }}>{t('settings.interactions.restrictedSubtitle')}</p>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <input type="text" placeholder={t('settings.interactions.searchPlaceholder')} value={restrictSearch}
            onChange={e => searchForRestrict(e.target.value)} style={inputSt} onFocus={fi} onBlur={fb} />
          {restrictSearch.trim() && (
            <div className="st-search-dropdown">
              {restrictSearching ? (
                <div style={{ padding: 14, fontSize: 13, color: 'rgba(234,243,238,0.6)' }}>{t('settings.interactions.searching')}</div>
              ) : restrictResults.length === 0 ? (
                <div style={{ padding: 14, fontSize: 13, color: 'rgba(234,243,238,0.6)' }}>{t('settings.interactions.noUsersFound')}</div>
              ) : restrictResults.map(u => (
                <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <img src={getImageUrl(u.profileImage)} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} onError={e => e.target.src = DEFAULT_AVATAR} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#fdfdf9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.organizationName || u.name || u.username}</div>
                  <button onClick={() => restrictUser(u._id)} className="st-btn-secondary st-btn-sm" style={{ flexShrink: 0 }}>
                    {t('settings.interactions.restrictBtn')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {restrictedUsers.length === 0 ? (
          <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.5)', margin: 0 }}>{t('settings.interactions.noneRestricted')}</p>
        ) : restrictedUsers.map(u => (
          <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <img src={getImageUrl(u.profileImage)} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }} onError={e => e.target.src = DEFAULT_AVATAR} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#fdfdf9' }}>{u.organizationName || u.name || u.username}</div>
            <button onClick={() => unrestrictUser(u._id)} className="st-btn-secondary st-btn-sm" style={{ flexShrink: 0 }}>
              {t('settings.interactions.unrestrictBtn')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderArchive = () => (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><Archive size={18} color="#ffd873" /> {t('settings.archive.title')}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t('settings.archive.subtitle')}</p>
      </div>
      {loadingArchive ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.16)', borderTopColor: '#ffcb47', borderRadius: '50%', animation: 'st-spin 1s linear infinite', margin: '0 auto 12px' }} />
        </div>
      ) : archivedPosts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px' }}>
          <Archive size={40} color="rgba(255,255,255,0.25)" style={{ marginBottom: 12 }} />
          <h3 style={{ color: '#fdfdf9', marginBottom: 8 }}>{t('settings.archive.emptyTitle')}</h3>
          <p style={{ color: 'rgba(234,243,238,0.5)', fontSize: 14, margin: 0 }}>{t('settings.archive.emptyBody')}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }} className="st-posts-grid">
          {archivedPosts.map(post => (
            <div key={post._id} className="st-list-card">
              {post.image && <img src={getImageUrl(post.image)} alt="" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />}
              {post.content && <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.85)', lineHeight: 1.5, padding: '10px 12px 0', margin: 0, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{post.content}</p>}
              <div style={{ padding: 12 }}>
                <button onClick={() => unarchivePost(post._id)} className="st-btn-secondary" style={{ width: '100%' }}>
                  {t('settings.archive.unarchiveBtn')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderActivity = () => {
    const items = activity?.[activityFilter] || [];
    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={18} color="#ffd873" /> {t('settings.activity.title')}</h3>
          <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t('settings.activity.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {[['liked', t('settings.activity.filterLiked')], ['saved', t('settings.activity.filterSaved')], ['commented', t('settings.activity.filterCommented')], ['searches', t('settings.activity.filterSearches')]].map(([k, l]) => (
            <button key={k} onClick={() => setActivityFilter(k)} className={`st-pill${activityFilter === k ? ' active' : ''}`}>
              {l}
            </button>
          ))}
        </div>
        {loadingActivity ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.16)', borderTopColor: '#ffcb47', borderRadius: '50%', animation: 'st-spin 1s linear infinite', margin: '0 auto 12px' }} />
          </div>
        ) : items.length === 0 ? (
          <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.5)', textAlign: 'center', padding: '30px 0' }}>{t('settings.activity.empty')}</p>
        ) : activityFilter === 'searches' ? (
          <div style={card}>
            {items.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <Search size={14} color="rgba(234,243,238,0.6)" />
                <span style={{ fontSize: 13, color: '#fdfdf9' }}>{s.query}</span>
                <span style={{ fontSize: 11, color: 'rgba(234,243,238,0.5)', marginLeft: 'auto' }}>{new Date(s.createdAt).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={card}>
            {items.map((item, i) => {
              const post = activityFilter === 'commented' ? item.post : item;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                  {post?.image && <img src={getImageUrl(post.image)} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {activityFilter === 'commented' && <div style={{ fontSize: 12.5, color: 'rgba(234,243,238,0.85)', marginBottom: 2 }}>"{item.content}"</div>}
                    <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post?.content || '—'}</div>
                    <div style={{ fontSize: 11, color: 'rgba(234,243,238,0.5)' }}>{post?.author?.name || post?.author?.username} · {new Date(item.createdAt).toLocaleDateString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderAccessibility = () => (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>♿ {t('settings.accessibility.title')}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t('settings.accessibility.subtitle')}</p>
      </div>
      <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t('settings.accessibility.reduceMotion')}</h4>
          <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: 0 }}>{t('settings.accessibility.reduceMotionDesc')}</p>
        </div>
        <label style={{ position: 'relative', display: 'inline-block', width: 46, height: 26, flexShrink: 0 }}>
          <input type="checkbox" checked={reduceMotion} onChange={e => { setReduceMotion(e.target.checked); localStorage.setItem('reduceMotion', String(e.target.checked)); }} style={{ opacity: 0, width: 0, height: 0 }} />
          <span style={{ position: 'absolute', inset: 0, background: reduceMotion ? 'linear-gradient(135deg,#ffd873,#d69a17)' : 'rgba(255,255,255,0.18)', borderRadius: 26, transition: 'background .2s', cursor: 'pointer' }} onClick={() => { const next = !reduceMotion; setReduceMotion(next); localStorage.setItem('reduceMotion', String(next)); }} />
          <span style={{ position: 'absolute', top: 3, left: reduceMotion ? 23 : 3, width: 20, height: 20, background: reduceMotion ? '#3a2a05' : '#fdfdf9', borderRadius: '50%', transition: 'left .2s', pointerEvents: 'none' }} />
        </label>
      </div>
    </div>
  );

  const renderDataAndPermissions = () => (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><Save size={18} color="#ffd873" /> {t('settings.dataExport.title')}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t('settings.dataExport.subtitle')}</p>
      </div>
      <div style={card}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t('settings.dataExport.downloadHeading')}</h4>
        <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 14px' }}>{t('settings.dataExport.downloadDesc')} {t('settings.dataExport.scopeHint')}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            ['all', t('settings.dataExport.scopeAll')],
            ['profile', t('settings.dataExport.scopeProfile')],
            ['programs', t('settings.dataExport.scopePrograms')],
            ['buddy', t('settings.dataExport.scopeBuddy')],
            ['messages', t('settings.dataExport.scopeMessages')],
          ].map(([scope, label]) => (
            <button key={scope} onClick={() => handleDataExport(scope)} disabled={exportingData}
              className={scope === 'all' ? 'st-btn-primary' : 'st-btn-secondary'} style={{ opacity: exportingData ? 0.7 : 1 }}>
              <Save size={14} /> {exportingData && exportingScope === scope ? t('settings.dataExport.preparing') : label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ margin: '28px 0 20px' }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>{t('settings.dataExport.permissionsTitle')}</h3>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t('settings.dataExport.permissionsSubtitle')}</p>
      </div>
      <div style={card}>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.8)', lineHeight: 1.6, margin: '0 0 10px' }}>
          {t('settings.dataExport.permissionsPara1')}
        </p>
        <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.8)', lineHeight: 1.6, margin: 0 }}>
          {t('settings.dataExport.permissionsPara2')}
        </p>
      </div>
    </div>
  );

  const FRAME_OPTIONS = [
    { key: '', label: t("settings.pro.frameNone"), swatch: '#dde4de' },
    { key: 'gold', label: t("settings.pro.frameGold"), swatch: '#d69b1f' },
    { key: 'emerald', label: t("settings.pro.frameEmerald"), swatch: '#1f9e6e' },
    { key: 'royal', label: t("settings.pro.frameRoyal"), swatch: '#6a2fc9' },
  ];
  const DESIGN_OPTIONS = [
    { key: '', label: t("settings.pro.designNone"), swatch: 'linear-gradient(135deg,#dde4de,#dde4de)' },
    { key: 'sunset', label: t("settings.pro.designSunset"), swatch: 'linear-gradient(135deg,#c2501f,#f4b483)' },
    { key: 'ocean', label: t("settings.pro.designOcean"), swatch: 'linear-gradient(135deg,#1f5c8a,#7fc1e8)' },
    { key: 'forest', label: t("settings.pro.designForest"), swatch: 'linear-gradient(135deg,#1c5c37,#7fce9e)' },
    { key: 'midnight', label: t("settings.pro.designMidnight"), swatch: 'linear-gradient(135deg,#362a6e,#9b8ce8)' },
  ];

  const renderPro = () => {
    const isPro = !!proInfo?.isPro;
    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><Crown size={18} color="#ffd873" /> {t("settings.pro.title")}</h3>
          <p style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)', margin: 0 }}>{t("settings.pro.subtitle")}</p>
        </div>

        <div style={{ ...card, ...(isPro ? { borderColor: 'rgba(255,203,71,0.55)', boxShadow: '0 8px 30px rgba(8,22,16,0.35), 0 0 0 1px rgba(255,203,71,0.25) inset, 0 1px 0 rgba(255,255,255,0.18) inset' } : {}) }}>
          {isPro ? (
            <div style={{ fontSize: 14, fontWeight: 800, color: '#ffd873' }}>
              {t("settings.pro.statusActive", { date: proInfo.proExpiresAt ? new Date(proInfo.proExpiresAt).toLocaleDateString() : '' })}
            </div>
          ) : (
            <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(234,243,238,0.65)' }}>{t("settings.pro.statusNone")}</div>
          )}
          <button onClick={() => navigate('/pro')} className="st-btn-primary" style={{ marginTop: 10 }}>
            {t("settings.pro.learnMore")}
          </button>
        </div>

        <div style={{ ...card, opacity: isPro ? 1 : 0.55, pointerEvents: isPro ? 'auto' : 'none' }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 12px' }}>{t("settings.pro.frameTitle")}</h4>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {FRAME_OPTIONS.map(opt => (
              <button key={opt.key} onClick={() => setFrameChoice(opt.key)} className={`st-swatch-btn${frameChoice === opt.key ? ' active' : ''}`}
                style={{ cursor: isPro ? 'pointer' : 'default' }}>
                <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#fff', outline: `3px solid ${opt.swatch}`, outlineOffset: 2 }} />
                <span style={{ fontSize: 11, color: 'rgba(234,243,238,0.75)' }}>{opt.label}</span>
              </button>
            ))}
          </div>

          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 12px' }}>{t("settings.pro.designTitle")}</h4>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {DESIGN_OPTIONS.map(opt => (
              <button key={opt.key} onClick={() => setDesignChoice(opt.key)} className={`st-swatch-btn${designChoice === opt.key ? ' active' : ''}`}
                style={{ cursor: isPro ? 'pointer' : 'default' }}>
                <span style={{ width: 40, height: 26, borderRadius: 6, background: opt.swatch }} />
                <span style={{ fontSize: 11, color: 'rgba(234,243,238,0.75)' }}>{opt.label}</span>
              </button>
            ))}
          </div>

          <button onClick={handleSaveCustomization} disabled={savingCustomization || !isPro} className="st-btn-primary">
            <Save size={14} /> {savingCustomization ? t("settings.pro.saving") : t("settings.pro.save")}
          </button>
        </div>

        <div style={{ ...card, opacity: isPro ? 1 : 0.55, pointerEvents: isPro ? 'auto' : 'none' }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t("settings.pro.channelTitle")}</h4>
          <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 14px' }}>{t("settings.pro.channelSubtitle")}</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[
              { key: 'platform', label: t("settings.pro.channelPlatform") },
              { key: 'email', label: t("settings.pro.channelEmail") },
              { key: 'telegram', label: proInfo?.telegramChatId ? t("settings.pro.channelTelegram") : `${t("settings.pro.channelTelegram")} (${t("settings.pro.linkTelegram")})` },
            ].map(opt => (
              <button key={opt.key} disabled={savingChannel || linkingTelegram} onClick={() => handleSaveChannel(opt.key)} className={`st-picker-btn${channelChoice === opt.key ? ' active' : ''}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {isPro && (
            <button onClick={async () => {
              try {
                const res = await fetch(`${API}/gamification/send-my-matches`, { method: 'POST', headers: authHeaders() });
                const d = await res.json();
                toast(res.ok && d.success ? t("settings.pro.testSent") : (d.error || t("settings.pro.customizationError")));
              } catch { toast(t("settings.pro.customizationError")); }
            }}
              className="st-btn-secondary st-btn-sm" style={{ marginTop: 10 }}>
              {t("settings.pro.sendTest")}
            </button>
          )}
        </div>

        <div style={{ ...card, opacity: isPro ? 1 : 0.55, pointerEvents: isPro ? 'auto' : 'none' }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t("settings.pro.timingTitle")}</h4>
          <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 14px' }}>{t("settings.pro.timingSubtitle")}</p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 6 }}>{t("settings.pro.deliveryHour")}</label>
            <select value={matchPrefs.matchDeliveryHour} disabled={savingMatchPrefs}
              onChange={(e) => handleSaveMatchPrefs({ matchDeliveryHour: Number(e.target.value) })}
              className="st-select">
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#fdfdf9', marginBottom: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={matchPrefs.matchReminders} disabled={savingMatchPrefs}
              onChange={(e) => handleSaveMatchPrefs({ matchReminders: e.target.checked })} />
            {t("settings.pro.remindersLabel")}
          </label>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 6 }}>{t("settings.pro.detailLevel")}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['brief', 'detailed'].map(level => (
                <button key={level} disabled={savingMatchPrefs} onClick={() => handleSaveMatchPrefs({ matchDetailLevel: level })} className={`st-picker-btn${matchPrefs.matchDetailLevel === level ? ' active' : ''}`}>
                  {t(`settings.pro.detail_${level}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 6 }}>{t("settings.pro.sourceTitle")}</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {[['ai', t("settings.pro.sourceAi")], ['categories', t("settings.pro.sourceCategories")]].map(([key, label]) => (
                <button key={key} disabled={savingMatchPrefs} onClick={() => handleSaveMatchPrefs({ matchSource: key })} className={`st-picker-btn${matchPrefs.matchSource === key ? ' active' : ''}`}>
                  {label}
                </button>
              ))}
            </div>
            {matchPrefs.matchSource === 'ai' && (
              <p style={{ fontSize: 11.5, color: 'rgba(234,243,238,0.5)', margin: 0 }}>{t("settings.pro.sourceAiHint")}</p>
            )}
            {matchPrefs.matchSource === 'categories' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {MATCH_CATEGORIES.map(cat => {
                  const active = matchPrefs.matchCategories.includes(cat);
                  return (
                    <button key={cat} disabled={savingMatchPrefs} onClick={() => {
                      const next = active ? matchPrefs.matchCategories.filter(c => c !== cat) : [...matchPrefs.matchCategories, cat];
                      handleSaveMatchPrefs({ matchCategories: next });
                    }}
                      className={`st-chip${active ? ' selected' : ''}`}>
                      {t(`settings.pro.category_${cat}`)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={card}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}><Eye size={15} color="#ffd873" /> {t("settings.pro.viewsTitle")}</h4>
          <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 14px' }}>{t("settings.pro.viewsSubtitle")}</p>
          {!isPro && (
            <div style={{ fontSize: 13, color: 'rgba(234,243,238,0.75)' }}>
              {viewsCountToday === null
                ? t("settings.pro.loading")
                : t("settings.pro.viewsCountToday", { count: viewsCountToday })}
              <div style={{ fontSize: 12, color: 'rgba(234,243,238,0.5)', marginTop: 4 }}>{t("settings.pro.viewsLocked")}</div>
            </div>
          )}
          {isPro && loadingProfileViews && <div style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)' }}>{t("settings.pro.loading")}</div>}
          {isPro && !loadingProfileViews && profileViewsError && <div style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)' }}>{profileViewsError}</div>}
          {isPro && !loadingProfileViews && !profileViewsError && profileViews.length === 0 && (
            <div style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)' }}>{t("settings.pro.viewsEmpty")}</div>
          )}
          {isPro && profileViews.map((v, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
              <img src={getImageUrl(v.viewer?.profileImage)} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fdfdf9' }}>
                  {v.viewer?.organizationName || `${v.viewer?.name || ''} ${v.viewer?.surname || ''}`.trim() || v.viewer?.username || '—'}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(234,243,238,0.5)' }}>{new Date(v.viewedAt).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={card}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fdfdf9', margin: '0 0 4px' }}>{t("settings.pro.billingTitle")}</h4>
          <p style={{ fontSize: 12, color: 'rgba(234,243,238,0.6)', margin: '0 0 14px' }}>{t("settings.pro.billingSubtitle")}</p>

          {loadingBilling && <div style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)' }}>{t("settings.pro.loading")}</div>}
          {!loadingBilling && billingHistory.length === 0 && (
            <div style={{ fontSize: 13, color: 'rgba(234,243,238,0.65)' }}>{t("settings.pro.billingEmpty")}</div>
          )}
          {!loadingBilling && billingHistory.map((p, i) => (
            <div key={p.orderId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fdfdf9' }}>
                  {p.meta?.plan ? t(`pages.pro.plans.${p.meta.plan}`) : p.description} ({p.amount} AMD)
                </div>
                <div style={{ fontSize: 11, color: 'rgba(234,243,238,0.5)' }}>
                  {p.paidAt ? new Date(p.paidAt).toLocaleDateString() : ''}
                </div>
              </div>
              {p.refundStatus === 'none' && refundEligible(p) && (
                <button onClick={() => openRefundModal(p)} className="st-btn-secondary st-btn-sm">
                  {t("settings.pro.requestRefund")}
                </button>
              )}
              {p.refundStatus === 'none' && !refundEligible(p) && (
                <span style={{ fontSize: 11.5, color: 'rgba(234,243,238,0.4)' }}>{t("settings.pro.refundWindowExpired")}</span>
              )}
              {p.refundStatus === 'requested' && (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#ffd873' }}>{t("settings.pro.refundStatusRequested")}</span>
              )}
              {p.refundStatus === 'rejected' && (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#ff9b95' }}>{t("settings.pro.refundStatusRejected")}</span>
              )}
              {(p.refundStatus === 'refunded' || p.refundStatus === 'approved') && (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: '#4ade80' }}>{t("settings.pro.refundStatusRefunded")}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Sidebar nav items ──
  const navGroups = [
    {
      label: t("settings.sidebar.account"),
      items: [
        { id: 'account', icon: <User size={17} />, label: t("settings.sidebar.accountSettings") },
        { id: 'saved', icon: <><Heart size={17} color={activeTab==='saved'?'#ffd873':'#ff9b95'} /><Bookmark size={17} style={{marginLeft:-6}} /></>, label: t("settings.sidebar.savedLiked"), badge: savedPosts.length + likedPosts.length },
        { id: 'archive', icon: <Archive size={17} />, label: t("settings.sidebar.archive") },
        { id: 'activity', icon: <Clock size={17} />, label: t("settings.sidebar.yourActivity") },
      ],
    },
    {
      label: t("settings.sidebar.howOthersInteract"),
      items: [
        { id: 'interactions', icon: <MessageCircle size={17} />, label: t("settings.sidebar.interactionsLabel") },
      ],
    },
    {
      label: t("settings.sidebar.preferences"),
      items: [
        { id: 'privacy', icon: <Shield size={17} />, label: t("settings.sidebar.privacySecurity") },
        { id: 'accessibility', icon: <span style={{ fontSize: 15 }}>♿</span>, label: t("settings.sidebar.accessibility") },
        { id: 'data', icon: <Save size={17} />, label: t("settings.sidebar.dataPermissions") },
        { id: 'pro', icon: <Crown size={17} />, label: t("settings.sidebar.pro") },
        { id: 'support', icon: <MessageCircle size={17} />, label: t("settings.sidebar.support") },
      ],
    },
  ];

  const renderSupport = () => (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><MessageCircle size={18} color="#ffd873" /> {t("settings.sidebar.support")}</h2>
      <div className="st-support-wrap">
        <SupportChatPanel />
      </div>
    </div>
  );

  return (
    <div className="gw-settings-page" style={{ fontFamily: "'DM Sans',-apple-system,sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>

      {/* ── HEADER ── */}
      <div className="st-header">
        <div className="st-header-inner">
          <div>
            <h1 style={{ fontSize: 'clamp(20px,4vw,28px)', fontWeight: 800, margin: '0 0 4px', lineHeight: 1.15, color: '#fdfdf9', fontFamily: 'var(--font-display, inherit)', textShadow: '0 2px 18px rgba(0,0,0,0.25)' }}>{t("settings.title")}</h1>
            <p style={{ fontSize: 13, opacity: 0.75, margin: 0, color: 'rgba(234,243,238,0.85)' }}>{t("settings.subtitle")}</p>
          </div>
          <div className="st-header-user">
            <img src={user?.profileImage ? getImageUrl(user.profileImage) : DEFAULT_AVATAR} alt={user?.name}
              style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,203,71,0.4)', flexShrink: 0 }}
              onError={e => e.target.src = DEFAULT_AVATAR} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#fdfdf9' }}>{user?.name} {user?.surname}</div>
              <div style={{ fontSize: 12, opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(234,243,238,0.8)' }}>{user?.email}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── LAYOUT ── */}
      <div className="st-layout">

        {/* Sidebar — a normal in-flow horizontally-scrolling tab strip on
            mobile instead of a fixed overlay drawer (see st-sidebar mobile
            rules in Settings.css): a fixed-position element always ends up
            overlapping *something* else that's also fixed (the bottom nav
            bar, a floating toggle button) depending on scroll/viewport,
            which took several broken iterations to work around. Content
            that's simply part of the page's own flow can't overlap
            anything by construction, so it's used here instead. */}
        <div className="st-sidebar">
          {navGroups.map((group, gi) => (
            <React.Fragment key={group.label}>
              <div className="st-nav-group-label" style={{ borderTop: gi > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none', marginTop: gi > 0 ? 4 : 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(234,243,238,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{group.label}</div>
              </div>
              {group.items.map(item => (
                <button key={item.id} className={`st-nav-btn${activeTab === item.id ? ' active' : ''}`} onClick={() => handleTabChange(item.id)}>
                  {item.icon} <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge > 0 && <span className="st-nav-badge">{item.badge}</span>}
                </button>
              ))}
            </React.Fragment>
          ))}
          <div className="st-sidebar-footer">
            <button onClick={handleLogout} className="st-logout-btn">
              <LogOut size={16} /> {t("common.logout")}
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="st-main">
          {activeTab === 'account' && renderAccount()}
          {activeTab === 'saved' && renderSaved()}
          {activeTab === 'archive' && renderArchive()}
          {activeTab === 'activity' && renderActivity()}
          {activeTab === 'interactions' && renderInteractions()}
          {activeTab === 'privacy' && renderPrivacy()}
          {activeTab === 'accessibility' && renderAccessibility()}
          {activeTab === 'data' && renderDataAndPermissions()}
          {activeTab === 'pro' && renderPro()}
          {activeTab === 'support' && renderSupport()}
        </div>
      </div>

      {/* ── REFUND REQUEST MODAL ── */}
      {refundTarget && (
        <div className="st-modal-backdrop">
          <div className="st-modal" style={{ maxWidth: 420, textAlign: 'left' }}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#fdfdf9', marginBottom: 4, fontFamily: 'var(--font-display, inherit)' }}>
              {t("settings.pro.refundModalTitle")}
            </h3>
            <p style={{ fontSize: 12.5, color: 'rgba(234,243,238,0.6)', marginBottom: 16, lineHeight: 1.5 }}>
              {t("settings.pro.refundModalSubtitle")}
            </p>

            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 6 }}>
              {t("settings.pro.refundReasonLabel")}
            </label>
            <select value={refundReason} onChange={(e) => setRefundReason(e.target.value)} className="st-select" style={{ width: '100%', marginBottom: 16 }}>
              <option value="">{t("settings.pro.selectReason")}</option>
              {REFUND_REASONS.map((r) => (
                <option key={r} value={r}>{t(`settings.pro.reason_${r}`)}</option>
              ))}
            </select>

            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 8 }}>
              {t("settings.pro.ratingLabel")}
            </label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRefundRating(n)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }} aria-label={`${n} star`}>
                  <Star size={22} fill={n <= refundRating ? '#ffd873' : 'none'} color={n <= refundRating ? '#ffd873' : 'rgba(234,243,238,0.4)'} />
                </button>
              ))}
            </div>

            <label style={{ fontSize: 12, fontWeight: 700, color: 'rgba(234,243,238,0.75)', display: 'block', marginBottom: 6 }}>
              {t("settings.pro.commentLabel")}
            </label>
            <textarea value={refundComment} onChange={(e) => setRefundComment(e.target.value)} placeholder={t("settings.pro.commentPlaceholder")}
              rows="3" style={{ ...inputSt, resize: 'vertical', marginBottom: 16 }} onFocus={fi} onBlur={fb} />

            <button
              type="button"
              onClick={() => { setRefundTarget(null); handleTabChange('support'); }}
              className="st-text-link"
              style={{ display: 'block', fontSize: 12.5, marginBottom: 18, background: 'none', border: 'none', color: '#ffcb47', fontWeight: 700, cursor: 'pointer', padding: 0 }}
            >
              {t("settings.pro.talkToSupport")} {t("settings.pro.talkToSupportLink")} →
            </button>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setRefundTarget(null)} className="st-btn-secondary">{t("postModal.cancel")}</button>
              <button onClick={handleSubmitRefund} disabled={submittingRefund} className="st-btn-primary">
                {submittingRefund ? t("settings.pro.submittingRefund") : t("settings.pro.submitRefund")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ── */}
      {showDeleteModal && (
        <div className="st-modal-backdrop">
          <div className="st-modal" style={{ maxWidth: 400, textAlign: 'left' }}>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#fdfdf9', marginBottom: 16, fontFamily: 'var(--font-display, inherit)' }}>{t("settings.deleteModal.heading")}</h3>
            <select value={deleteReason} onChange={e => setDeleteReason(e.target.value)}
              className="st-select" style={{ width: '100%', marginBottom: 12 }}>
              <option value="">{t("settings.deleteModal.selectReason")}</option>
              <option value="privacy">{t("settings.deleteModal.reasonPrivacy")}</option>
              <option value="not_useful">{t("settings.deleteModal.reasonNotUseful")}</option>
              <option value="too_many_emails">{t("settings.deleteModal.reasonTooManyEmails")}</option>
              <option value="other">{t("settings.deleteModal.reasonOther")}</option>
            </select>
            {deleteReason === "other" && (
              <textarea placeholder={t("settings.deleteModal.writeReason")} value={customReason} onChange={e => setCustomReason(e.target.value)}
                rows="3" style={{ ...inputSt, resize: 'vertical', marginBottom: 12 }} onFocus={fi} onBlur={fb} />
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteModal(false)} className="st-btn-secondary">{t("postModal.cancel")}</button>
              <button onClick={() => { if (!deleteReason) return toast(t("settings.deleteModal.pleaseSelectReason")); setShowDeleteModal(false); setShowFinalConfirm(true); }}
                className="st-btn-danger">OK</button>
            </div>
          </div>
        </div>
      )}

      {/* ── FINAL CONFIRM ── */}
      {showFinalConfirm && (
        <div className="st-modal-backdrop">
          <div className="st-modal" style={{ maxWidth: 360, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, background: 'rgba(239,68,68,0.16)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <AlertTriangle size={28} color="#ff9b95" />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fdfdf9', marginBottom: 8, fontFamily: 'var(--font-display, inherit)' }}>{t("settings.deleteModal.areYouSure")}</h3>
            <p style={{ fontSize: 14, color: 'rgba(234,243,238,0.68)', marginBottom: 20, lineHeight: 1.6 }}>{t("settings.deleteModal.cannotBeUndone")}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowFinalConfirm(false)} className="st-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>{t("postModal.cancel")}</button>
              <button onClick={handleDeleteAccount} className="st-btn-danger" style={{ flex: 1, justifyContent: 'center' }}>{t("settings.deleteModal.yesDelete")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;